import { createSelector } from 'reselect';
import { get } from 'lodash';
import queryString from 'query-string';
import ReactMapboxGl from 'react-mapbox-gl';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Signer } from '@aws-amplify/core';

import { makeFilterString } from '../../filters';
import { getQueryString } from '../../api';

export const getFilter = createSelector(
  ({ filtersSelected }) => filtersSelected,
  ({ filters }) => filters,
  getQueryString,
  makeFilterString,
);

const layerCache = {};
const DEFAULT_STYLE = {
  type: 'line',
  paint: {
    'line-color': '#E2603B',
  },
};
const HOVER_STYLE = {
  type: 'line',
  paint: {
    'line-color': '#290CFA',
    'line-width': 2,
  },
};
export const getLayers = createSelector(
  ({ reports }) => reports.find(({ id }) => id === 'PARCEL_REPORT'),
  ({ layers }) => layers,
  ({ layersSelected }) => layersSelected,
  ({ customLayers }) => customLayers,
  ({ newCustomLayers }) => newCustomLayers,
  ({ selectedSurveyLayers }) => selectedSurveyLayers,
  ({ filters }) => filters,
  ({ customLayersConfig }) => customLayersConfig,
  ({ selectedSidePanelReport }) => selectedSidePanelReport,
  getQueryString,
  ({ serverConfig }) => serverConfig,
  ({ mapHoverFilters }) => mapHoverFilters,
  (
    singleParcelReport,
    layers,
    layersSelected,
    customLayers,
    newCustomLayers,
    selectedSurveyLayers,
    filters,
    customLayersConfig,
    selectedSidePanelReport,
    querystring,
    serverConfig,
    mapHoverFilters,
  ) => {
    const areDotsSupported = get(
      customLayersConfig,
      'styleTypeOptions',
      [],
    ).includes('circle');

    const surveyLayers = Object.entries(selectedSurveyLayers).reduce(
      (acc, [key, survey]) => {
        if (layerCache[key]) {
          acc[key] = layerCache[key];
          return acc;
        }

        if (key && survey) {
          const layerDefinition = {
            key,
            dataKey: 'parcel_id',
            style: DEFAULT_STYLE,
            geom: 'geom',
            label: survey.name,
            filters: `parcel_id=insurvey%3A${survey.id}`,
          };
          acc[key] = layerDefinition;
          layerCache[key] = layerDefinition;
        }
        return acc;
      },
      {},
    );

    const standardLayers = Object.entries(layersSelected).reduce(
      (acc, [key, val]) => {
        if (!val) return acc;
        if (!layers[key]) return acc;

        if (layerCache[key]) {
          acc[key] = layerCache[key];
          return acc;
        }

        if (layers[key] && layers[key].type === 'COMPOSITE_LAYER') {
          layers[key].layers.forEach((innerLayer) => {
            const layerDefinition = {
              type: innerLayer.type,
              key: innerLayer.key,
              dataStreamField: innerLayer.dataStreamField,
              orLess: innerLayer.orLess,
              dataKey: innerLayer.key,
              style: innerLayer.style,
              map: innerLayer.map,
              preventFiltering: innerLayer.preventFiltering,
              geom: innerLayer.geom,
              label: innerLayer.label,
              source: innerLayer.source,
              unit: innerLayer.unit,
              filterString: innerLayer.filterString,
              forceDots: areDotsSupported ? innerLayer.forceDots : false,
            };
            if (innerLayer.style.type === 'raster') {
              layerDefinition.url = innerLayer.url;
            }
            acc[innerLayer.key] = layerDefinition;
          });

          return acc;
        }

        if (key && layers[key]) {
          const layer = layers[key];
          const layerDefinition = {
            key,
            type: layer.type,
            dataStreamField: layer.dataStreamField,
            dataKey: key,
            discrete: layer.discrete,
            orLess: layer.orLess,
            style: layer.style,
            map: layer.map,
            preventFiltering: layer.preventFiltering,
            geom: layer.geom,
            label: layer.label,
            source: layer.source,
            unit: layer.unit,
            filterString: layer.filterString,
            forceDots: areDotsSupported ? layer.forceDots : false,
          };
          if (key && layers[key].style.type === 'raster') {
            layerDefinition.url = layer.url;
          }
          acc[key] = layerDefinition;
          layerCache[key] = layerDefinition;
        }
        return acc;
      },
      {},
    );

    const newLayers = Object.values(newCustomLayers).reduce((acc, layer) => {
      if (!layer) return acc;
      if (!Object.keys(layer.filters).length) return acc;
      const newLayerFilters = filters.find(({ type }) => type === 'PARENT')
        ? filters.find(({ source }) => source === layer.source).filters
        : filters;
      acc[layer.key] = {
        key: layer.key,
        dataKey: layer.dataKey || 'parcel_id',
        style: layer.style,
        geom: layer.geom,
        filters: makeFilterString(layer.filters, newLayerFilters, querystring),
        label: layer.label ? layer.label : 'New Custom Layer',
        source: layer.source,
        isNew: true,
        isCustom: true,
        forceDots:
          typeof layer.forceDots === 'boolean' || layer.source === 'submarkets'
            ? layer.forceDots
            : areDotsSupported,
      };
      return acc;
    }, {});

    const selectedCustomLayers = Object.entries(layersSelected).reduce(
      (acc, [key, val]) => {
        if (!val) return acc;
        const layer = customLayers[key];
        if (key && layer) {
          acc[layer.key] = {
            key: layer.key,
            dataKey: layer.dataKey || 'parcel_id',
            style: layer.style,
            geom: layer.geom,
            filters: makeFilterString(
              layer.filters,
              (filters[0].type === 'PARENT')
                ? filters.find(({ source }) => source === layer.source).filters
                : filters,
              querystring,
            ),
            label: layer.label,
            source: layer.source,
            forceDots:
              typeof layer.forceDots === 'boolean' ||
              layer.source === 'submarkets'
                ? layer.forceDots
                : areDotsSupported,
            isCustom: true,
          };
        }
        return acc;
      },
      {},
    );

    const results = {
      ...newLayers,
      ...selectedCustomLayers,
      ...standardLayers,
      ...surveyLayers,
    };

    if (
      selectedSidePanelReport &&
      serverConfig.namespace !== 'mi_medc_dev' &&
      serverConfig.namespace !== 'public/mi_medc_public'
    ) {
      const identifier = singleParcelReport.identifier || 'parcel_id';

      const highlightedParcelFilters = queryString.stringify({
        ...mapHoverFilters,
        [identifier]: selectedSidePanelReport,
      });
      const highlightedParcel = {
        key: 'highlighted_parcel',
        dataKey: identifier,
        style: HOVER_STYLE,
        geom: 'geom',
        filters: highlightedParcelFilters,
        label: 'highlighted_parcel',
        forceDots: areDotsSupported,
      };
      results.highlightedParcel = highlightedParcel;
    }

    return results;
  },
);

const hoveredParcelCache = {};
const getStyleType = ({ mapSettings }) => (mapSettings.MAIN_MAP.zoom < 12.8 ? 'circle' : 'line');
const snakeCase = (str) => str.toLowerCase().replace(' ', '_');

const getHoveredParcelLayer = createSelector(
  ({ hoveredParcel }) => hoveredParcel.parcel,
  getStyleType,
  ({ serverConfig }) => serverConfig,
  ({ customLayersConfig }) => customLayersConfig,
  ({ mapHoverFilters }) => mapHoverFilters,
  (hoveredParcel, styleType, serverConfig, customLayersConfig, mapHoverFilters) => {
    if (serverConfig.namespace === 'medc-intel') return null;
    if (!hoveredParcel) return null;

    const idName = hoveredParcel.fips ? 'fips' : 'parcel_id';
    const cachedHoveredParcelId = get(hoveredParcelCache, `hoveredParcel["${idName}"]`);

    if (
      cachedHoveredParcelId === hoveredParcel[idName] &&
      hoveredParcelCache.styleType === styleType
    ) {
      return hoveredParcelCache.hoveredParcelLayer;
    }

    const areDotsSupported = get(
      customLayersConfig,
      'styleTypeOptions',
      [],
    ).includes('circle');

    const dataKey = snakeCase(idName);
    const filters = queryString.stringify({
      ...mapHoverFilters,
      [dataKey]: hoveredParcel[idName],
    });

    const hoveredParcelLayer = {
      key: `hovered_parcel_${styleType}`,
      dataKey,
      style: HOVER_STYLE,
      geom: 'geom',
      filters,
      label: 'hovered_parcel',
      forceDots: areDotsSupported,
    };

    hoveredParcelCache.hoveredParcel = hoveredParcel;
    hoveredParcelCache.styleType = styleType;
    hoveredParcelCache.hoveredParcelLayer = hoveredParcelLayer;

    return hoveredParcelLayer;
  },
);

const cache = {
  layers: null,
  mapSettings: null,
  filters: null,
  drawEnabled: false,
};

const getTileSourceOptions = createSelector(
  ({ serverConfig }) => serverConfig,
  (serverConfig) => ({
    type: 'vector',
    tiles: [
      `${serverConfig.baseUrl}/tiles/${serverConfig.namespace}/{z}/{x}/{y}`,
    ],
  }),
);

export const hoveredParcelState = createSelector(
  getHoveredParcelLayer,
  getTileSourceOptions,
  getStyleType,
  (hoveredParcelLayer, tileSourceOptions, styleType) => ({
    tileSourceOptions,
    layer: hoveredParcelLayer,
    styleType,
    tileSize: 124,
  }),
);

export const mapState = createSelector(
  getLayers,
  getFilter,
  getTileSourceOptions,
  ({ serverConfig }) => serverConfig,
  ({ mapSettings }) => mapSettings,
  ({ reports }) => reports,
  ({ newCustomLayers }) => !!Object.keys(newCustomLayers).length,
  ({ drawEnabled }) => drawEnabled,
  ({ mapStyle }) => mapStyle,
  ({ selectedSurveyLayers }) => selectedSurveyLayers,
  (
    layers,
    filters,
    tileSourceOptions,
    serverConfig,
    mapSettings,
    reports,
    hasNewLayers,
    drawEnabled,
    mapStyle,
    selectedSurveyLayers,
  ) => {
    if (
      layers === cache.layers &&
      cache.mapSettings &&
      cache.zoom === mapSettings.MAIN_MAP.zoom &&
      cache.filters === filters &&
      cache.drawEnabled === drawEnabled &&
      cache.selectedSurveyLayers === selectedSurveyLayers
    ) {
      return {
        tileSourceOptions,
        reports,
        drawEnabled,
        selectedSurveyLayers,
        center: cache.mapSettings.MAIN_MAP.center,
        zoom: cache.zoom,
        layers,
        filters,
        tileEndpoint: `${serverConfig.baseUrl}/tiles/${serverConfig.namespace}/{z}/{x}/{y}`,
        baseUrl: serverConfig.baseUrl,
        hasNewLayers,
        mapStyle,
      };
    }
    cache.mapSettings = mapSettings;
    cache.selectedSurveyLayers = selectedSurveyLayers;
    cache.layers = layers;
    cache.filters = filters;
    cache.drawEnabled = drawEnabled;
    cache.zoom = [mapSettings.MAIN_MAP.zoom];

    return {
      selectedSurveyLayers,
      tileSourceOptions,
      reports,
      drawEnabled,
      bbox: mapSettings.MAIN_MAP.bbox,
      center: mapSettings.MAIN_MAP.center,
      zoom: cache.zoom,
      layers,
      filters,
      tileEndpoint: `${serverConfig.baseUrl}/tiles/${serverConfig.namespace}/{z}/{x}/{y}`,
      baseUrl: serverConfig.baseUrl,
      hasNewLayers,
      mapStyle,
    };
  },
);

const endpointInfo = {
  region: 'us-east-2',
  service: 'execute-api',
};

const makeMapbox = (credentials, baseUrl, mapboxArguments = {}) => {
  const mapbox = new ReactMapboxGl({
    accessToken:
      'pk.eyJ1IjoiZHluYW1vYWRtaW4iLCJhIjoiY2p4ejZuZWY0MDFjeDNnbzBnNDE5cDhqMyJ9.WiGVupwHBQ3IGFp8f9FUPQ',
    transformRequest: (url, resourceType) => {
      if (
        resourceType === 'Tile' &&
        url.startsWith(baseUrl) &&
        !url.startsWith('http://localhost')
      ) {
        const params = {
          url,
          data: null,
          headers: {},
          host: baseUrl.split('//')[1],
          method: 'GET',
          responseType: 'application/vnd.mapbox-vector-tile',
          timeout: 0,
          path: url.split(0, baseUrl.length),
        };

        const signedParamsMap = Signer.sign(params, credentials, endpointInfo);
        delete signedParamsMap.headers.host;
        return {
          url,
          headers: signedParamsMap.headers,
        };
      }
      return {
        url,
      };
    },
    ...mapboxArguments,
    preserveDrawingBuffer: true,
  });
  return mapbox;
};

export const getMapbox = createSelector(
  ({ credentials }) => credentials,
  ({ serverConfig }) => serverConfig.baseUrl,
  ({ drawEnabled }) => drawEnabled,
  makeMapbox,
);
const CONFIG = {
  preserveDrawingBuffer: true,
};
export const getPrintableMapbox = createSelector(
  ({ credentials }) => credentials,
  ({ serverConfig }) => serverConfig.baseUrl,
  () => CONFIG,
  makeMapbox,
);
